import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CollectionStatusEnum } from "@kanpla/api-contract";
import { faRefresh } from "@fortawesome/pro-regular-svg-icons";

import { t, T } from "@repo/transifex";
import { Button, DialogError } from "~/components";
import { cn } from "~/lib/utils";
import { useGetCollectOrder } from "~/api/collect/hooks/use-get-collect-order";
import { useUser } from "~/api/users/hooks/use-user";
import { ErrorDisplay } from "~/components/collect/error-display";
import { collectionStatusBg } from "~/components/collect/collection-status-styles";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

import { OrdersBasketContainer } from "./basket/orders-basket-container";
import { OrderDetailsPageLayout } from "./order-details-page-layout";
import { UserInfoContainer } from "./user-info-container";

export const CollectChildOrderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get("childId");
  const groupName = searchParams.get("groupName");

  const {
    data: order,
    status: orderStatus,
    refetch: refetchOrder,
    isRefetching: isOrderRefetching
  } = useGetCollectOrder({
    childId: childId ?? undefined
  });
  const {
    status: userStatus,
    refetch: refetchUser,
    isRefetching: isUserRefetching
  } = useUser({
    childId
  });

  const { data: till } = useCurrentTill();

  if (!childId || !groupName) {
    return (
      <DialogError hideSupport>
        <Button
          onClick={() => {
            navigate("/collect", { replace: true });
          }}
        >
          {t("Reload")}
        </Button>
      </DialogError>
    );
  }

  if (userStatus === "error") {
    return (
      <OrderDetailsPageLayout
        className="bg-interface-negative-1"
        leftColumn={
          <ErrorDisplay>
            <ErrorDisplay.Title>
              <T _str="Something went wrong!" />
            </ErrorDisplay.Title>
            <ErrorDisplay.Description>
              <T _str="We couldn’t load the user’s profile" />
            </ErrorDisplay.Description>
            <ErrorDisplay.Action
              variant="danger"
              onClick={() => refetchUser()}
              loading={isUserRefetching}
              icon={faRefresh}
            >
              <T _str="Try again" />
            </ErrorDisplay.Action>
          </ErrorDisplay>
        }
      />
    );
  }

  if (orderStatus === "error") {
    return (
      <OrderDetailsPageLayout
        className="bg-interface-negative-1"
        leftColumn={
          <ErrorDisplay>
            <ErrorDisplay.Title>
              <T _str="Something went wrong!" />
            </ErrorDisplay.Title>
            <ErrorDisplay.Description>
              <T _str="We couldn’t load the user’s order" />
            </ErrorDisplay.Description>
            <ErrorDisplay.Action
              variant="danger"
              onClick={() => refetchOrder()}
              loading={isOrderRefetching}
              icon={faRefresh}
            >
              <T _str="Try again" />
            </ErrorDisplay.Action>
          </ErrorDisplay>
        }
      />
    );
  }

  // Display NO_ORDERS screen only if the till has no pre-order creation
  if (
    orderStatus === "success" &&
    order?.collectionStatus === CollectionStatusEnum.NO_ORDERS &&
    till?.type !== "collect-lite" &&
    till?.type === "collect" &&
    !till?.config.hasPreOrderCreation
  ) {
    return <Navigate to="/pos/sell" replace />;
  }

  return (
    <OrderDetailsPageLayout
      className={cn(
        "flex size-full flex-1 flex-row",
        orderStatus === "pending" && "bg-interface-neutral-4",
        order?.collectionStatus &&
          collectionStatusBg({ status: order.collectionStatus })
      )}
      leftColumn={<UserInfoContainer childId={childId} />}
      rightColumn={
        <OrdersBasketContainer childId={childId} groupName={groupName} />
      }
    />
  );
};
